<script setup>
import WrPaymentModal from "~/components/global/WrPaymentModal.vue";

import tabs from "./package-content.json";
import wordpress_content from "./index-content.json";
import WrPricingCard from "~/components/global/WrPricingCard.vue";

const meta_tags = wordpress_content.meta_tags;
const faq = wordpress_content.faq;
const cta = wordpress_content.cta;
const packages = wordpress_content.packages;

definePageMeta({
  layout: "laravel-layout",
});

useHead({
  title:
    "WordPress website maintenance and support packages/plans start at $49/monthly",
  meta: meta_tags.meta,
});

const visible = ref(false);
const paymentPlan = ref(false);
const selectedPlan = ref("");
const content = ref({});
const openPricingModal = (price, duration, feature) => {
  content.value = {
    heading: feature.heading,
    paragraph: feature.text,
    highlightedContent: feature?.highlightContent ?? null,
    buttontxt: `${price} ${duration}`,
  };
  visible.value = true;
};
const handlePaymentPlanTrue = (plan) => {
  selectedPlan.value = plan;
  paymentPlan.value = true;
};

const handleCloseAndPaymentOpen = (shouldPaymentPopupOpen = null) => {
  if (shouldPaymentPopupOpen) {
    selectedPlan.value = shouldPaymentPopupOpen;
    visible.value = false;
    paymentPlan.value = true;
  }
};

const pricing_section = ref(null);

function scrollToPricing() {
  if (pricing_section.value) {
    const yOffset = -80;
    const y =
      pricing_section.value.getBoundingClientRect().top +
      window.pageYOffset +
      yOffset;

    window.scrollTo({ top: y, behavior: "smooth" });
  }
}
</script>

<template>
  <section class="">
    <div class="max-w-screen-xl mx-auto px-4">
      <!-- Hero Section -->
      <div
        class="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 py-16 max-sm:pt-4 relative"
      >
        <!-- Decorative dots -->

        <!-- Text Content -->
        <div>
          <h1 class="font-extrabold leading-tight">
            <WrGradientText>
              WordPress MonthlyMaintenance Plans from WebReinvent
            </WrGradientText>
          </h1>
          <p class="mt-2 text-lg !leading-[1.8] max-w-xl">
            Website maintenance has become a crucial part of running a
            successful website. A website without regular updates becomes
            outdated & useless, leaving it vulnerable to being taken over by
            competitors that do not have these problems.
          </p>
          <WrButton @click="scrollToPricing" container-class="mt-4"
            >View Packages</WrButton
          >
        </div>
        <!-- Image -->
        <div class="relative">
          <NuxtImg
            src="/images/hero-wp-header-v1.webp"
            width="588"
            height="367"
            alt="Laravel Maintenance Illustration"
            loading="lazy"
            format="webp"
          />
        </div>
      </div>
    </div>
  </section>
  <section ref="pricing_section" class="z-10 py-10">
    <div class="max-w-screen-lg mx-auto overflow-hidden px-4">
      <h2 class="lg:text-center l font-extrabold leading-tight">
        <WrGradientText>
          {{ packages.main_heading }}
        </WrGradientText>
      </h2>
      <p class="mt-2 text-lg !leading-[1.8] tracking-[0.5px] lg:text-center">
        {{ packages.sub_text }}
      </p>
    </div>

    <WrPricingCard
      :tabs="tabs"
      @paymentPlantrue="handlePaymentPlanTrue"
      @openModal="openPricingModal"
    />
  </section>

  <WrBannerCTA
    :heading="cta.title"
    :button-text="cta.linkText"
    :paragraph-text="cta.subheading"
    :handle-click="scrollToPricing"
  />

  <section class="py-6 lg:py-12">
    <div class="px-4 max-w-screen-xl mx-auto">
      <h2 class="lg:text-center">
        <WrGradientText append-class="!leading-[1]">
          {{ faq.main_heading }}
        </WrGradientText>
      </h2>
      <div
        class="tracking-[0.5px] mb-14 mx-auto lg:px-20 text-lg lg:text-center"
      >
        {{ faq.sub_text }}
      </div>
      <WrAccordion :Content="faq.content" />
      <div
        class="mt-6 bg-red-100/50 border-l-4 border-[#d50b00] text-[#cf2722] text-xs p-4 rounded lg:mx-4"
      >
        <p>
          <strong>Disclaimer:</strong> Laravel and the Laravel logo are
          trademarks of Taylor Otwell. Copyright © 2011–2021
          <NuxtLink
            to="https://laravel.com"
            class="underline hover:no-underline"
            >Laravel LLC</NuxtLink
          >, used under license agreement.
        </p>
      </div>
    </div>
  </section>

  <section class="relative z-10 pb-8">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="text-center">
        <WrGradientText text="Trusted for excellence and efficiency" />
        <p class="text-lg text-[var(--foreground)]">
          Webreinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </h2>
      <WrTestimonialCardCarousel class="mt-12" />
      <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
        >View All</WrButton
      >
    </div>
  </section>
  <WrPricingModal
    :isOpen="visible"
    @close="visible = false"
    @CloseAndPaymentOpen="handleCloseAndPaymentOpen"
    class="z-30"
    :content="content"
  />

  <WrPaymentModal
    :isOpen="paymentPlan"
    @close="paymentPlan = false"
    class="z-30"
    :plan="selectedPlan"
    page="wordpress"
  />
</template>
